import type { CarId } from "./protocol";

export const TELEMETRY_BUCKET_MS = 100;
export const THROTTLE_RAW_FULL_SCALE = 120;

export interface Stage2VehicleResponse {
  turn_rad_s: number;
  drive_g: number;
  brake_g: number;
}

export interface Stage2RacePose {
  x_m: number;
  y_m: number;
  heading_rad: number;
}

export interface Stage2TelemetryStatus {
  available: boolean;
  reason: "available" | "source-gap";
  previous_race_time_ms: number;
  next_race_time_ms: number;
  gap_duration_ms: number;
  threshold_ms: number;
}

export type RelativeBand = "low" | "middle_low" | "middle_high" | "high";

export interface Stage2RelativeMetric {
  current: number;
  band: RelativeBand | null;
  interpretation: string;
}

export interface Stage2TireThermalState {
  values: Record<"front_left" | "front_right" | "rear_left" | "rear_right", number | null>;
  observed_tire_count: number;
  coverage: "complete" | "partial";
  available_average: number | null;
  front_average: number | null;
  rear_average: number | null;
  hottest_position: string | null;
  coldest_position: string | null;
  spread: number | null;
  recent_change_10s: number | null;
  recent_trend: "rising" | "steady" | "falling";
  relative_band: RelativeBand | null;
  below_normal_positions: string[];
  above_normal_positions: string[];
  all_tires_ready: boolean | null;
  overall: "unknown" | "cold" | "warming_up" | "ready" | "hot" | "uneven";
}

export interface Stage2RelativeContext {
  baseline: "per-car formation lap through goal quartiles";
  metrics: Record<string, Stage2RelativeMetric>;
  recent_motion: {
    window_seconds: number;
    speed_change_kmh: number;
    rpm_change: number;
    throttle_change: number;
    steering_abs_change_deg: number;
  };
  tire_thermal_state: Stage2TireThermalState;
}

export interface Stage2DriverIntent {
  steering_sign: -1 | 1;
  turn_gain_rad_per_s_per_deg: number;
  vehicle_tau_seconds: number;
  smoothing_time_seconds: number;
  horizon_ms: 2500;
  intent_horizon_ms: 800;
  source: "classical-driver-model-output-lookahead-rollout";
  response_source: "classical-vehicle-model-output-lookahead-rollout";
  speed_source: "recorded-observation-lookahead";
  steering_convention: "effective-clockwise-positive";
  steering_rollout: {
    offset_ms: number;
    steering_deg: number;
    speed_kmh: number;
    vehicle_yaw_response_rad_s: number;
    causal_id: string;
  }[];
}

export interface Stage2TelemetryHistoryPoint {
  race_time_ms: number;
  steering: number;
  throttle: number;
  brake: number;
  yaw_response: number;
  longitudinal_response: number;
  drive_g: number;
  accel_long_g: number | null;
  accel_lat_g: number | null;
  accel_vert_g: number | null;
}

export interface Stage2TelemetryRecord {
  schema_version: "stage2-race-telemetry/v1";
  car_id: CarId;
  seek_generation: number;
  tick_index: number;
  race_time_ms: number;
  lap_counter: number;
  distlap_m: number;
  rpm: number;
  speed_kmh: number;
  steering: number;
  throttle: number;
  brake: number;
  gear: number;
  accel_long_g: number | null;
  accel_lat_g: number | null;
  accel_vert_g: number | null;
  yaw_response: number;
  longitudinal_response: number;
  vehicle_response: Stage2VehicleResponse;
  relative_context: Stage2RelativeContext;
  race_situation: Stage2RaceSituation | null;
  telemetry_status: Stage2TelemetryStatus;
  pose: Stage2RacePose;
  driver_intent: Stage2DriverIntent;
  shadow_causal_id: string;
  history: Stage2TelemetryHistoryPoint[];
}

export interface Stage2RaceCarIdentity {
  car_id: string;
  car_number: string;
  driver_name_ja: string;
}

export interface Stage2RaceSituation {
  schema_version: "stage2-race-situation/v1";
  causal_id: string;
  sample_race_time_ms: number;
  phase: "formation" | "race";
  field_car_count: 24;
  on_track: {
    position: number | null;
    terminology_ja: ["見かけ上の順位", "表の順位"];
    car_ahead: Stage2RaceCarIdentity | null;
    gap_to_car_ahead_m: number | null;
  };
  effective: {
    position: number | null;
    terminology_ja: ["実質的な順位", "裏の順位"];
    basis: "mandatory-stop-adjusted-with-expected-pit-loss";
    expected_pit_loss_m: number;
    is_estimate: true;
  };
  physical_ahead: {
    car: Stage2RaceCarIdentity | null;
    along_course_gap_m: number | null;
  };
  mandatory_pit_stop: { window_open: boolean; completed: boolean };
}

export interface TelemetryRequestIdentity {
  carId: CarId;
  seekGeneration: number;
  raceTimeBucketMs: number;
  revision: number;
}

const finite = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);
const nullableFinite = (value: unknown): boolean => value === null || finite(value);
const relativeBands = new Set(["low", "middle_low", "middle_high", "high", null]);

const validRelativeContext = (value: unknown): value is Stage2RelativeContext => {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<Stage2RelativeContext>;
  const metrics = item.metrics && Object.values(item.metrics);
  const recent = item.recent_motion;
  const tire = item.tire_thermal_state;
  return item.baseline === "per-car formation lap through goal quartiles"
    && Array.isArray(metrics) && metrics.length >= 1
    && metrics.every(metric => finite(metric.current) && relativeBands.has(metric.band)
      && typeof metric.interpretation === "string" && metric.interpretation.length > 0)
    && !!recent && [recent.window_seconds, recent.speed_change_kmh, recent.rpm_change,
      recent.throttle_change, recent.steering_abs_change_deg].every(finite)
    && !!tire && Object.values(tire.values ?? {}).length === 4
    && Object.values(tire.values ?? {}).every(nullableFinite)
    && Number.isInteger(tire.observed_tire_count) && Number(tire.observed_tire_count) >= 0
    && Number(tire.observed_tire_count) <= 4
    && (tire.coverage === "complete" || tire.coverage === "partial")
    && [tire.available_average, tire.front_average, tire.rear_average, tire.spread,
      tire.recent_change_10s].every(nullableFinite)
    && ["rising", "steady", "falling"].includes(String(tire.recent_trend))
    && relativeBands.has(tire.relative_band)
    && Array.isArray(tire.below_normal_positions) && Array.isArray(tire.above_normal_positions)
    && (tire.all_tires_ready === null || typeof tire.all_tires_ready === "boolean")
    && ["unknown", "cold", "warming_up", "ready", "hot", "uneven"].includes(String(tire.overall));
};

const validRaceCarIdentity = (value: unknown): value is Stage2RaceCarIdentity => {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<Stage2RaceCarIdentity>;
  return typeof item.car_id === "string" && /^car\d+$/.test(item.car_id)
    && typeof item.car_number === "string" && /^\d+$/.test(item.car_number)
    && typeof item.driver_name_ja === "string" && item.driver_name_ja.length > 0;
};

const nullablePosition = (value: unknown): boolean => value === null
  || (Number.isInteger(value) && Number(value) >= 1 && Number(value) <= 24);

const validRaceSituation = (value: unknown): value is Stage2RaceSituation => {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<Stage2RaceSituation>;
  const onTrack = item.on_track;
  const effective = item.effective;
  const physical = item.physical_ahead;
  return item.schema_version === "stage2-race-situation/v1"
    && typeof item.causal_id === "string" && item.causal_id.length > 0
    && Number.isInteger(item.sample_race_time_ms) && ["formation", "race"].includes(String(item.phase))
    && item.field_car_count === 24
    && !!onTrack && nullablePosition(onTrack.position)
    && Array.isArray(onTrack.terminology_ja)
    && onTrack.terminology_ja[0] === "見かけ上の順位" && onTrack.terminology_ja[1] === "表の順位"
    && (onTrack.car_ahead === null || validRaceCarIdentity(onTrack.car_ahead))
    && nullableFinite(onTrack.gap_to_car_ahead_m)
    && !!effective && nullablePosition(effective.position)
    && Array.isArray(effective.terminology_ja)
    && effective.terminology_ja[0] === "実質的な順位" && effective.terminology_ja[1] === "裏の順位"
    && effective.basis === "mandatory-stop-adjusted-with-expected-pit-loss"
    && finite(effective.expected_pit_loss_m) && effective.expected_pit_loss_m > 0
    && effective.is_estimate === true
    && !!physical && (physical.car === null || validRaceCarIdentity(physical.car))
    && nullableFinite(physical.along_course_gap_m)
    && !!item.mandatory_pit_stop && typeof item.mandatory_pit_stop.window_open === "boolean"
    && typeof item.mandatory_pit_stop.completed === "boolean";
};

export const telemetryRaceTimeBucket = (raceTimeMs: number): number =>
  Math.max(0, Math.floor(Math.max(0, raceTimeMs) / TELEMETRY_BUCKET_MS) * TELEMETRY_BUCKET_MS);

export const telemetryRetryDelayMs = (failedAttempts: number): number => {
  const attempt = Math.max(1, Math.floor(failedAttempts));
  return Math.min(1_000, 80 * 2 ** Math.min(4, attempt - 1));
};

export const isStage2TelemetryRecord = (
  value: unknown, identity: TelemetryRequestIdentity,
): value is Stage2TelemetryRecord => {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<Stage2TelemetryRecord>;
  const validHistory = Array.isArray(item.history) && item.history.length > 0 && item.history.length <= 101
    && item.history.every((point, index) => Number.isInteger(point.race_time_ms)
      && point.race_time_ms <= identity.raceTimeBucketMs
      && (index === 0 || point.race_time_ms > item.history![index - 1].race_time_ms)
      && finite(point.steering) && finite(point.throttle) && finite(point.brake)
      && finite(point.yaw_response) && finite(point.longitudinal_response) && finite(point.drive_g)
      && nullableFinite(point.accel_long_g)
      && nullableFinite(point.accel_lat_g)
      && nullableFinite(point.accel_vert_g));
  return item.schema_version === "stage2-race-telemetry/v1"
    && item.car_id === identity.carId
    && item.seek_generation === identity.seekGeneration
    && item.race_time_ms === identity.raceTimeBucketMs
    && Number.isInteger(item.tick_index) && Number(item.tick_index) >= 0
    && Number.isInteger(item.lap_counter)
    && Number.isInteger(item.gear)
    && finite(item.distlap_m) && finite(item.rpm) && finite(item.speed_kmh)
    && finite(item.steering) && finite(item.throttle) && finite(item.brake)
    && nullableFinite(item.accel_long_g)
    && nullableFinite(item.accel_lat_g)
    && nullableFinite(item.accel_vert_g)
    && finite(item.yaw_response) && finite(item.longitudinal_response)
    && !!item.vehicle_response
    && finite(item.vehicle_response.turn_rad_s)
    && finite(item.vehicle_response.drive_g)
    && finite(item.vehicle_response.brake_g)
    && validRelativeContext(item.relative_context)
    && (item.race_situation === null || validRaceSituation(item.race_situation))
    && !!item.telemetry_status
    && typeof item.telemetry_status.available === "boolean"
    && item.telemetry_status.reason === (item.telemetry_status.available ? "available" : "source-gap")
    && Number.isInteger(item.telemetry_status.previous_race_time_ms)
    && Number.isInteger(item.telemetry_status.next_race_time_ms)
    && item.telemetry_status.next_race_time_ms >= item.telemetry_status.previous_race_time_ms
    && Number.isInteger(item.telemetry_status.gap_duration_ms)
    && item.telemetry_status.gap_duration_ms
      === item.telemetry_status.next_race_time_ms - item.telemetry_status.previous_race_time_ms
    && Number.isInteger(item.telemetry_status.threshold_ms) && item.telemetry_status.threshold_ms > 0
    && !!item.pose && finite(item.pose.x_m) && finite(item.pose.y_m) && finite(item.pose.heading_rad)
    && !!item.driver_intent && [-1, 1].includes(item.driver_intent.steering_sign)
    && finite(item.driver_intent.turn_gain_rad_per_s_per_deg)
    && item.driver_intent.turn_gain_rad_per_s_per_deg > 0
    && finite(item.driver_intent.vehicle_tau_seconds) && item.driver_intent.vehicle_tau_seconds > 0
    && finite(item.driver_intent.smoothing_time_seconds) && item.driver_intent.smoothing_time_seconds > 0
    && item.driver_intent.horizon_ms === 2500
    && item.driver_intent.intent_horizon_ms === 800
    && item.driver_intent.source === "classical-driver-model-output-lookahead-rollout"
    && item.driver_intent.response_source === "classical-vehicle-model-output-lookahead-rollout"
    && item.driver_intent.speed_source === "recorded-observation-lookahead"
    && item.driver_intent.steering_convention === "effective-clockwise-positive"
    && Array.isArray(item.driver_intent.steering_rollout)
    && item.driver_intent.steering_rollout.length >= 1
    && item.driver_intent.steering_rollout.every((point, index) =>
      Number.isInteger(point.offset_ms) && point.offset_ms >= 0 && point.offset_ms <= 2500
      && (index === 0 ? point.offset_ms === 0
        : point.offset_ms > item.driver_intent!.steering_rollout[index - 1].offset_ms)
      && finite(point.steering_deg) && finite(point.speed_kmh) && point.speed_kmh >= 0
      && finite(point.vehicle_yaw_response_rad_s)
      && typeof point.causal_id === "string" && point.causal_id.length > 0)
    && typeof item.shadow_causal_id === "string" && item.shadow_causal_id.length > 0
    && validHistory;
};

export const isCurrentTelemetryRequest = (
  active: TelemetryRequestIdentity | null, candidate: TelemetryRequestIdentity,
): boolean => active !== null
  && active.revision === candidate.revision
  && active.carId === candidate.carId
  && active.seekGeneration === candidate.seekGeneration
  && active.raceTimeBucketMs === candidate.raceTimeBucketMs;

export const throttleRawPercent = (raw: number): number =>
  Math.max(0, Math.min(100, raw / THROTTLE_RAW_FULL_SCALE * 100));

export interface Stage2ConversationMotionContext {
  telemetry: { speed_kmh: number; rpm: number };
  operations: {
    steering_deg: number;
    throttle_raw: number;
    front_brake_pressure: number;
    gear: number;
  };
  vehicle_response: Stage2VehicleResponse;
  relative_context: Stage2RelativeContext;
  race_situation: Stage2RaceSituation | null;
}

export const telemetryToConversationMotionContext = (
  record: Stage2TelemetryRecord,
): Stage2ConversationMotionContext => ({
  telemetry: { speed_kmh: record.speed_kmh, rpm: record.rpm },
  operations: {
    steering_deg: record.steering,
    throttle_raw: record.throttle,
    front_brake_pressure: record.brake,
    gear: record.gear,
  },
  vehicle_response: { ...record.vehicle_response },
  relative_context: record.relative_context,
  race_situation: record.race_situation,
});
